import { cn } from "@/lib/utils"
import { Button, ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import {
  CheckCircleIcon,
  InfoIcon,
  PlusCircleIcon,
} from "@/components/ui/icons"

export interface PricingCardFeature {
  label: string
  hasInfo?: boolean
  aiBadge?: boolean
  /** When set, replaces the inert info icon with a clickable slot (e.g. a ProductInfoButton). Overrides hasInfo. */
  infoSlot?: React.ReactNode
  /** Optional sub-label rendered next to the main label (used for add-on prices). */
  suffix?: string
}

export interface PricingCardHighlight {
  icon: React.ReactNode
  label: string
}

export interface PricingCardFeatureSection {
  /** Optional section heading rendered above the feature list. */
  label?: string
  features: (string | PricingCardFeature)[]
}

export interface PricingCardProps {
  icon?: React.ReactNode
  name: string
  nameInfo?: boolean
  nameInfoSlot?: React.ReactNode
  subtitle?: string
  description: string
  price: string
  currency?: string
  /** Strikethrough price shown above the main price. Used when insurance reimbursement reduces the headline. */
  originalPrice?: string
  /** Footnote rendered below the price (e.g. "Insurance covers CHF 700 via Helsana COMPLETA"). */
  priceNote?: string
  ctaLabel: string
  ctaVariant?: "primary" | "primary-alt"
  ctaHref?: string
  ctaTarget?: string
  ctaRel?: string
  highlights?: PricingCardHighlight[]
  featuresLabel?: string
  features?: (string | PricingCardFeature)[]
  /**
   * Glyph used next to each feature row. "check" reads as "included"
   * (Core's standalone list), "plus" reads as "additional" — pair with
   * "Everything in X, plus …" headers on Advanced / Pro.
   */
  featuresIcon?: "check" | "plus"
  /** When set, takes precedence over `features` — renders multiple labeled sections in row 4. */
  featureSections?: PricingCardFeatureSection[]
  aiBadgeLabel?: string
  highlighted?: boolean
  badgeLabel?: string
  onCtaClick?: () => void
  className?: string
}

export function PricingCard({
  icon,
  name,
  nameInfo = false,
  nameInfoSlot,
  subtitle,
  description,
  price,
  currency = "CHF",
  originalPrice,
  priceNote,
  ctaLabel,
  ctaVariant = "primary-alt",
  ctaHref,
  ctaTarget,
  ctaRel,
  highlights,
  featuresLabel = "Includes",
  features,
  featuresIcon = "check",
  featureSections,
  aiBadgeLabel = "AI",
  highlighted = false,
  badgeLabel,
  onCtaClick,
  className,
}: PricingCardProps) {
  return (
    <div
      data-testid="pricing-card"
      className={cn(
        "relative row-span-4 grid min-w-[311px] grid-rows-subgrid gap-y-3 rounded-[16px] border border-white bg-white/80 p-6 transition-[background-color,box-shadow] duration-300 ease-out hover:bg-white hover:shadow-[0_12px_32px_-12px_rgba(15,23,42,0.08)]",
        highlighted && "border-aubergine-500 ring-aubergine-200 ring-1",
        badgeLabel && "pill-magic-stroke pill-magic-stroke-always",
        className
      )}
    >
      {badgeLabel && (
        <div className="absolute top-0 right-12 z-10 -translate-y-[calc(50%+2px)]">
          <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block rounded-full px-5 py-1 font-medium normal-case">
            {badgeLabel}
          </span>
        </div>
      )}

      {(nameInfoSlot || nameInfo) && (
        <div className="absolute top-4 right-4">
          {nameInfoSlot ?? <InfoIcon size={22} />}
        </div>
      )}

      {/* Row 1: Header (small circle icon + name inline, then full-width subtitle + description) */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <h3 className="type-headline-md text-neutral-900">{name}</h3>
        </div>

        {subtitle && (
          <p className="type-body-md text-neutral-500">{subtitle}</p>
        )}

        <p className="type-body-lg text-neutral-900">{description}</p>
      </div>

      {/* Row 2: Price + CTA */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <div className="flex min-h-8 items-baseline gap-2">
            {price ? (
              <>
                {originalPrice && (
                  <span className="type-body-md text-neutral-400 line-through">
                    {currency} {originalPrice}
                  </span>
                )}
                <span className="type-body-md text-neutral-500">
                  {currency}
                </span>
                <span className="type-headline-sm text-neutral-900">
                  {price}
                </span>
              </>
            ) : (
              // Pre-launch EUR markets (DE/NL) don't yet have authoritative
              // pricing. Show a holding line rather than fabricated CHF→EUR
              // figures. [TODO: confirm DE pricing] — remove once buildFallback
              // returns authoritative EUR amounts.
              <span className="type-body-md text-neutral-500">—</span>
            )}
          </div>
          {priceNote && (
            <p className="type-body-sm text-neutral-500">{priceNote}</p>
          )}
        </div>

        {ctaHref ? (
          <a
            href={ctaHref}
            target={ctaTarget}
            rel={ctaRel}
            onClick={onCtaClick}
            className={cn(
              buttonVariants({ variant: ctaVariant }),
              "group relative w-full overflow-hidden !px-10"
            )}
          >
            <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
              {ctaLabel}
            </span>
            <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
              <ArrowRightIcon />
            </span>
          </a>
        ) : (
          <Button
            variant={ctaVariant}
            className="group relative w-full overflow-hidden !px-10"
            onClick={onCtaClick}
            iconRight={
              <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                <ArrowRightIcon />
              </span>
            }
          >
            <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
              {ctaLabel}
            </span>
          </Button>
        )}
      </div>

      {/* Row 3: Highlights */}
      {highlights && highlights.length > 0 ? (
        <div className="flex items-start">
          {highlights.map((highlight, i) => (
            <div
              key={i}
              className="flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2"
            >
              <div className="flex size-10 items-center justify-center">
                {highlight.icon}
              </div>
              <p className="type-body-sm text-center font-medium text-neutral-900">
                {highlight.label}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div />
      )}

      {/* Row 4: Features */}
      {featureSections && featureSections.length > 0 ? (
        <div className="flex flex-col gap-4">
          {featureSections.map((section, sIdx) => (
            <div key={sIdx} className="flex flex-col gap-2">
              {section.label && (
                <div className="px-2">
                  <span className="type-label-md text-neutral-500">
                    {section.label}
                  </span>
                </div>
              )}
              <FeatureList
                features={section.features}
                aiBadgeLabel={aiBadgeLabel}
              />
            </div>
          ))}
        </div>
      ) : features && features.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="px-2">
            <span className="type-label-md text-neutral-500">
              {featuresLabel}
            </span>
          </div>
          <FeatureList
            features={features}
            aiBadgeLabel={aiBadgeLabel}
            featuresIcon={featuresIcon}
          />
        </div>
      ) : (
        <div />
      )}
    </div>
  )
}

function FeatureList({
  features,
  aiBadgeLabel,
  featuresIcon = "check",
}: {
  features: (string | PricingCardFeature)[]
  aiBadgeLabel: string
  featuresIcon?: "check" | "plus"
}) {
  return (
    <div className="flex flex-col">
      {features.map((feature, i) => {
        const item =
          typeof feature === "string"
            ? { label: feature, hasInfo: false }
            : feature

        return (
          <div
            key={i}
            className="flex min-h-10 items-center gap-2 rounded-lg px-2 py-1.5"
          >
            {featuresIcon === "plus" ? (
              <PlusCircleIcon className="flex-shrink-0" />
            ) : (
              <CheckCircleIcon className="flex-shrink-0" />
            )}
            <span className="type-body-md md:type-body-lg flex flex-1 flex-wrap items-center gap-2 text-neutral-900">
              {item.label}
              {item.suffix && (
                <span className="text-neutral-500">{item.suffix}</span>
              )}
              {item.aiBadge && (
                <span className="bg-aubergine-100 text-aubergine-700 type-label-sm inline-flex items-center rounded-full px-2 py-0.5 leading-none font-medium">
                  {aiBadgeLabel}
                </span>
              )}
            </span>
            {item.infoSlot
              ? item.infoSlot
              : item.hasInfo && (
                  <InfoIcon size={16} className="flex-shrink-0" />
                )}
          </div>
        )
      })}
    </div>
  )
}
